import { AmenitiesEnum, EquipmentAvailabilitiesEnum } from 'types/generated/client';

/**
 * Shared display data for the club-profile amenity/equipment/social fields.
 * Consumed by both the edit form (ManageClubProfile) and the read-only
 * overview (ClubOverview) so labels and groupings stay in one place.
 */

/** Human label for every amenity enum value (incl. legacy values older venues may still have). */
export const AMENITY_LABELS: Record<AmenitiesEnum, string> = {
  [AmenitiesEnum.Lights]: 'Lighting',
  [AmenitiesEnum.AirConditioning]: 'Air conditioning',
  [AmenitiesEnum.Restrooms]: 'Restrooms',
  [AmenitiesEnum.Shower]: 'Shower',
  [AmenitiesEnum.FreeWifi]: 'Free Wi-Fi',
  [AmenitiesEnum.Water]: 'Water fountains',
  [AmenitiesEnum.Restaurant]: 'Restaurant',
  [AmenitiesEnum.Bar]: 'Bar',
  [AmenitiesEnum.Cafe]: 'Café / snack bar',
  [AmenitiesEnum.OutdoorSeating]: 'Outdoor seating',
  [AmenitiesEnum.ProShop]: 'Pro shop',
  [AmenitiesEnum.EquipmentRental]: 'Equipment rental',
  [AmenitiesEnum.WheelchairAccessible]: 'Wheelchair accessible',
  [AmenitiesEnum.AccessibleParking]: 'Accessible parking',
  [AmenitiesEnum.ChildFriendly]: 'Child friendly',
  [AmenitiesEnum.DogFriendly]: 'Dog friendly areas',
  [AmenitiesEnum.Parking]: 'Parking',
  // Legacy values not offered in the new design but kept so existing data renders.
  [AmenitiesEnum.Food]: 'Food / Restaurant',
  [AmenitiesEnum.LockerRooms]: 'Locker rooms',
  [AmenitiesEnum.Backboard]: 'Backboard',
  [AmenitiesEnum.Training]: 'Training / Lessons',
};

export interface AmenityCategory {
  title: string;
  amenities: AmenitiesEnum[];
}

/** Amenity groupings shown in the edit form (matches the Figma categorisation). */
export const AMENITY_CATEGORIES: AmenityCategory[] = [
  {
    title: 'Facilities & Play',
    amenities: [AmenitiesEnum.Lights, AmenitiesEnum.AirConditioning],
  },
  {
    title: 'Comfort & Essentials',
    amenities: [
      AmenitiesEnum.Restrooms,
      AmenitiesEnum.Shower,
      AmenitiesEnum.FreeWifi,
      AmenitiesEnum.Water,
    ],
  },
  {
    title: 'Food & Social',
    amenities: [
      AmenitiesEnum.Restaurant,
      AmenitiesEnum.Bar,
      AmenitiesEnum.Cafe,
      AmenitiesEnum.OutdoorSeating,
    ],
  },
  {
    title: 'Services',
    amenities: [AmenitiesEnum.ProShop, AmenitiesEnum.EquipmentRental],
  },
  {
    title: 'Accessibility',
    amenities: [AmenitiesEnum.WheelchairAccessible, AmenitiesEnum.AccessibleParking],
  },
  {
    title: 'Environment & Extras',
    amenities: [AmenitiesEnum.ChildFriendly, AmenitiesEnum.DogFriendly, AmenitiesEnum.Parking],
  },
];

/** Amenity enum values offered in the new form (flattened from the categories). */
export const SELECTABLE_AMENITIES: AmenitiesEnum[] = AMENITY_CATEGORIES.flatMap(
  (category) => category.amenities,
);

export const EQUIPMENT_LABELS: Record<EquipmentAvailabilitiesEnum, string> = {
  [EquipmentAvailabilitiesEnum.Provided]: 'Equipment provided',
  [EquipmentAvailabilitiesEnum.ForRent]: 'Equipment for rent',
  [EquipmentAvailabilitiesEnum.BringYourOwn]: 'Bring your own',
};

export const EQUIPMENT_OPTIONS: EquipmentAvailabilitiesEnum[] = [
  EquipmentAvailabilitiesEnum.Provided,
  EquipmentAvailabilitiesEnum.ForRent,
  EquipmentAvailabilitiesEnum.BringYourOwn,
];

/** Social link fields — `key` matches both the form field and the API payload field. */
export type SocialFieldKey =
  | 'instagramUrl'
  | 'facebookUrl'
  | 'youtubeUrl'
  | 'linkedinUrl'
  | 'twitterUrl';

export interface SocialPlatform {
  key: SocialFieldKey;
  label: string;
  placeholder: string;
}

export const SOCIAL_PLATFORMS: SocialPlatform[] = [
  { key: 'instagramUrl', label: 'Instagram', placeholder: 'https://instagram.com/yourclub' },
  { key: 'facebookUrl', label: 'Facebook', placeholder: 'https://facebook.com/yourclub' },
  { key: 'youtubeUrl', label: 'Youtube', placeholder: 'https://youtube.com/@yourclub' },
  { key: 'linkedinUrl', label: 'LinkedIn', placeholder: 'https://linkedin.com/company/yourclub' },
  { key: 'twitterUrl', label: 'X', placeholder: 'https://x.com/yourclub' },
];
